// apps/client/src/hooks/useEdmContacts.ts
//
// Typed wrappers over the `edmContactAction` callable (eDM Phase 1c S1/S2/S5):
// contacts list/add/edit/erase, bulk tagging, the tag registry and per-contact
// send history. Every request is `{ action, companyId, ...fields }`; every
// refusal is `HttpsError(code, message, { code: '<reason>' })`, mapped for
// display by edmContactErrorMessage below (falling back to utils/edm).
//
// Contacts are read ONLY through this callable. edm_contacts is client
// read/write false in firestore.rules; there is no Firestore SDK read here.
// The callable is company-wide only (super_admin, marketing, administrator
// with canSeeAllOffices), the same set RequireEdmImportAccess admits.

import { useCallback, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';
import { EDM_CALLABLE_OPTIONS } from './useEdmCampaigns';
import { useBrokerLabels } from '../pages/leads/edm/components/BrokerPicker';
import { edmErrorMessage, edmErrorReason } from '../utils/edm';

export type EdmContactStatusFilter = 'bounced' | 'complained' | 'suppressed';

export interface EdmContactFilters {
  q?: string;
  subscribed?: boolean;
  status?: EdmContactStatusFilter;
  tag?: string;
  importId?: string;
  brokerId?: string;
}

export interface EdmContact {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  tags: string[];
  subscribed: boolean;
  importId: string | null;
  brokerId: string | null;
  consentSource: string | null;
  suppression: { reason: string | null } | null;
  createdAt: string | null;
}

export type EdmTagKind = 'tag' | 'category' | 'event';

export interface EdmTagEntry {
  name: string;
  kind: EdmTagKind;
  eventDate: string | null;
  /** false = present on contacts but not in the registry (plan-v2 D34). */
  registered?: boolean;
}

export interface EdmContactHistoryOutcome {
  deliveredAt?: string | null;
  bouncedAt?: string | null;
  complainedAt?: string | null;
}

export interface EdmContactHistoryRow {
  campaignId: string;
  name: string;
  status: string;
  sentAt: string | null;
  ledgerStatus: string | null;
  outcome: EdmContactHistoryOutcome | string | null;
}

export interface AddContactInput {
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  tags?: string[];
  brokerId?: string;
  /** Structured consent (27 Oct 2026): how, when, and a note for 'other'. */
  consentMethod: EdmConsentMethod;
  /** 'YYYY-MM-DD', not in the future. */
  consentAgreedAt: string;
  /** Required for 'other' only; never sent otherwise. */
  consentNote?: string;
}

/**
 * Stored keys of the server's EDM_CONSENT_METHODS (edmManualConsent.ts).
 * The server composes and hashes the consent wording from the key; the
 * labels below are display only and mirror the server's.
 */
export type EdmConsentMethod = 'website_signup' | 'form_tickbox' | 'in_person_or_phone' | 'existing_client' | 'other';

export const EDM_CONSENT_METHOD_OPTIONS: ReadonlyArray<{ value: EdmConsentMethod; label: string }> = [
  { value: 'website_signup', label: 'Signed up on our website' },
  { value: 'form_tickbox', label: 'Ticked a box on a form (paper or online)' },
  { value: 'in_person_or_phone', label: 'Asked us in person or by phone' },
  { value: 'existing_client', label: 'Existing client who agreed to receive our emails' },
  { value: 'other', label: 'Other' },
];

/** Server cap on the "Other" note. */
export const EDM_CONSENT_NOTE_MAX = 500;

/** Changed fields only. Omitted = unchanged; '' = clear. */
export interface UpdateContactInput {
  contactId: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  tags?: string[];
  brokerId?: string;
}

export interface SaveTagInput {
  name: string;
  kind: EdmTagKind;
  eventDate?: string | null;
  /** Create only: an existing registry entry is left untouched. */
  createOnly?: boolean;
}

export interface EdmContactApi {
  listContacts: (
    filters: EdmContactFilters,
    page: number,
    pageSize: number,
  ) => Promise<{ contacts: EdmContact[]; total: number }>;
  /**
   * Cursor page (plan-v3 core A5; only when `edm_content` is on and no
   * status/search filter is set, see `isContactsCursorMode`). `cursor` ''
   * is the first page; `nextCursor` null means this is the last page.
   */
  listContactsPage: (
    filters: EdmContactFilters,
    cursor: string,
    pageSize: number,
  ) => Promise<{ contacts: EdmContact[]; total: number; nextCursor: string | null }>;
  addContact: (input: AddContactInput) => Promise<{ id: string }>;
  updateContact: (input: UpdateContactInput) => Promise<unknown>;
  deleteContact: (contactId: string) => Promise<unknown>;
  bulkTag: (
    contactIds: string[],
    add: string[],
    remove: string[],
  ) => Promise<{ updated?: number; failed?: number; missing?: number; capped?: number }>;
  listTags: () => Promise<{ tags: EdmTagEntry[] }>;
  saveTag: (input: SaveTagInput) => Promise<{ name: string; created: boolean }>;
  deleteTag: (name: string) => Promise<unknown>;
  contactHistory: (contactId: string) => Promise<{ history: EdmContactHistoryRow[] }>;
}

/** Server ceilings mirrored so the UI never sends what the callable refuses. */
export const EDM_CONTACTS_MAX_PAGE_SIZE = 100;
export const EDM_BULK_TAG_MAX_IDS = 500;

async function call<Res>(action: string, payload: Record<string, unknown>): Promise<Res> {
  const fn = httpsCallable<Record<string, unknown>, Res>(functions, 'edmContactAction', EDM_CALLABLE_OPTIONS);
  const res = await fn({ action, ...payload });
  return res.data;
}

/** Only the filters that are set; the server treats absent and null alike. */
export function compactFilters(f: EdmContactFilters): EdmContactFilters {
  const out: EdmContactFilters = {};
  const q = f.q?.trim();
  if (q) out.q = q;
  if (typeof f.subscribed === 'boolean') out.subscribed = f.subscribed;
  if (f.status) out.status = f.status;
  if (f.tag) out.tag = f.tag;
  const importId = f.importId?.trim();
  if (importId) out.importId = importId;
  if (f.brokerId) out.brokerId = f.brokerId;
  return out;
}

/**
 * Cursor paging applies only when `edm_content` is on AND neither the status
 * nor the search filter is set: those two keep the Phase 1c full-scan path on
 * the server (plan-v3 core A5), so the page keeps 1c offset paging for them.
 */
export function isContactsCursorMode(contentEnabled: boolean, filters: EdmContactFilters): boolean {
  const f = compactFilters(filters);
  return contentEnabled && f.status === undefined && f.q === undefined;
}

/** The callable surface bound to one company. */
export function useEdmContacts(companyId: string | undefined): EdmContactApi {
  const withCompany = useCallback(
    <Res,>(action: string, payload: Record<string, unknown> = {}) => {
      if (!companyId) return Promise.reject(new Error('companyId required'));
      return call<Res>(action, { companyId, ...payload });
    },
    [companyId],
  );

  return useMemo<EdmContactApi>(
    () => ({
      listContacts: (filters, page, pageSize) =>
        withCompany('listContacts', {
          filters: compactFilters(filters),
          page,
          pageSize: Math.min(pageSize, EDM_CONTACTS_MAX_PAGE_SIZE),
        }),
      listContactsPage: (filters, cursor, pageSize) =>
        withCompany('listContacts', {
          filters: compactFilters(filters),
          cursor,
          pageSize: Math.min(pageSize, EDM_CONTACTS_MAX_PAGE_SIZE),
        }),
      addContact: (input) => withCompany('addContact', { ...input }),
      updateContact: (input) => withCompany('updateContact', { ...input }),
      deleteContact: (contactId) => withCompany('deleteContact', { contactId }),
      bulkTag: (contactIds, add, remove) => withCompany('bulkTag', { contactIds, add, remove }),
      listTags: () => withCompany('listTags'),
      saveTag: (input) => withCompany('saveTag', { ...input }),
      deleteTag: (name) => withCompany('deleteTag', { name }),
      contactHistory: (contactId) => withCompany('contactHistory', { contactId }),
    }),
    [withCompany],
  );
}

/** Contact-surface refusals first, then the shared eDM copy. */
export function edmContactErrorMessage(err: unknown): string {
  switch (edmErrorReason(err)) {
    case 'already_exists':
      return 'This email address is already in your contacts.';
    case 'suppressed':
      return 'This address is on your suppression list (unsubscribed, bounced, complained or erased) and cannot be added.';
    case 'manual_add_daily_cap':
      return 'You have added 100 contacts by hand today (Brisbane time). Import a file or try again tomorrow.';
    case 'contacts_company_wide_only':
      return 'Contacts can only be managed by a Super Admin, Marketing, or an Administrator who can see all offices.';
    case 'contacts_too_many':
      return 'Your contact list is too large to show here. Contact VALUTTEN support.';
    case 'contact_not_found':
      return 'That contact no longer exists. Refresh the list.';
    case 'invalid_email':
      return 'Enter a valid email address.';
    case 'invalid_broker':
      return 'Choose an active member of your team as the broker.';
    case 'consent_date_in_future':
      return 'The date they agreed cannot be in the future.';
    case 'consent_date_required':
    case 'consent_date_invalid':
      return 'Check the date they agreed.';
    case 'consent_note_required':
      return 'Describe how this person agreed.';
    case 'consent_method_required':
      return 'Choose how this person agreed to receive your emails.';
    case 'invalid_tag_name':
      return "Tag names use letters, numbers, spaces and - _ & ' only (up to 60 characters).";
    default:
      return edmErrorMessage(err);
  }
}

export interface EdmBrokerLabel {
  id: string;
  displayName: string;
}

/**
 * Broker roster for the broker filter and select (id = Auth UID, the id space
 * contacts store): the audience panel's `useBrokerLabels` loader, so both
 * pages read getBrokerLabels the same way. A failure degrades to an empty list.
 */
export function useEdmBrokerLabels(companyId: string | undefined): EdmBrokerLabel[] {
  return useBrokerLabels(companyId).brokers;
}
